import { useState, useEffect } from "react";
import fleetApi from "@/lib/fleetApi";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { fmtDayKey } from "@/lib/time";
import { pausedDriverIds } from "@/lib/pausedDrivers";

const DONE = /complete|cancel|no.?show/i;

export default function ScheduleSendDialog({ open, onOpenChange, date }) {
  const [rows, setRows] = useState(null);
  const [emails, setEmails] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRows(null); setResult(null);
    Promise.all([fleetApi.get(`/trips?date=${date}&limit=5000`), api.get("/driver-tools/contacts").catch(() => ({ data: { emails: {} } })), pausedDriverIds(), fleetApi.get("/drivers").catch(() => ({ data: [] }))])
      .then(([t, c, p, dr]) => {
        const full = Object.fromEntries((dr.data || []).map((d) => [d.id, d.full_name || d.name]));
        const by = {};
        (t.data || []).filter((x) => x.assigned_driver_id && !DONE.test(x.status || "")).forEach((x) => {
          by[x.assigned_driver_id] = by[x.assigned_driver_id] || { id: x.assigned_driver_id, name: full[x.assigned_driver_id] || x.assigned_driver_name || "Driver", count: 0, paused: p.has(x.assigned_driver_id) };
          by[x.assigned_driver_id].count += 1;
        });
        setEmails(c.data.emails || {});
        setRows(Object.values(by).sort((a, b) => a.name.localeCompare(b.name)));
      }).catch(() => setRows([]));
  }, [open, date]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/driver-tools/schedule/send", { date, emails });
      setResult(data);
      toast.success(`Schedule emailed to ${data.sent} driver${data.sent === 1 ? "" : "s"}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const ready = (rows || []).filter((r) => !r.paused && /\S+@\S+\.\S+/.test(emails[r.id] || "")).length;
  const resOf = (id) => result?.results?.find((x) => x.driver_id === id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-[#0f172a] text-white sm:max-w-xl" data-testid="schedule-send-dialog">
        <DialogHeader>
          <DialogTitle>Send schedules for {fmtDayKey(date)}</DialogTitle>
          <DialogDescription className="text-slate-400">Each driver gets an email listing their pickups, riders and addresses for the day. Emails are saved for next time.</DialogDescription>
        </DialogHeader>
        {!rows ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          : !rows.length ? <p className="rounded-md border border-slate-800 bg-slate-900/60 p-4 text-sm text-slate-300" data-testid="schedule-send-empty">No trips are assigned to drivers on this day yet. Assign them first, then send.</p>
          : (
            <ul className="space-y-2" data-testid="schedule-send-list">
              {rows.map((r) => <DriverRow key={r.id} r={r} email={emails[r.id] || ""} res={resOf(r.id)} onEmail={(v) => setEmails({ ...emails, [r.id]: v })} />)}
            </ul>
          )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="schedule-send-close">{result ? "Done" : "Cancel"}</Button>
          <Button onClick={send} disabled={sending || !ready || !!result} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="schedule-send-submit">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {result ? `Sent to ${result.sent}` : `Send to ${ready} driver${ready === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DriverRow({ r, email, res, onEmail }) {
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-slate-800 bg-slate-900/60 p-3 sm:flex-row sm:items-center" data-testid={`schedule-row-${r.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{r.name}</p>
        <p className="text-xs text-slate-400">{r.count} pickup{r.count === 1 ? "" : "s"}{r.paused ? " · inactive, won't be sent" : ""}</p>
        {res && (
          <p className={`mt-0.5 flex items-center gap-1 text-xs font-semibold ${res.status === "sent" ? "text-emerald-300" : "text-amber-300"}`} data-testid={`schedule-result-${r.id}`}>
            {res.status === "sent" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} {res.status === "sent" ? `Sent to ${res.email}` : res.detail}
          </p>
        )}
      </div>
      <Input type="email" value={email} disabled={r.paused} onChange={(e) => onEmail(e.target.value)} placeholder="driver@email.com"
        className="border-slate-700 bg-slate-900 text-sm text-white sm:w-56" data-testid={`schedule-email-${r.id}`} />
    </li>
  );
}
